/**
 * MappingSelect
 *
 * A DS2026-styled, optionally-searchable single-select used on the Map Columns
 * step in place of a native `<select>` (whose option list is OS-rendered and
 * can't take the DS white-surface / gray-hover / Inter treatment). Built on the
 * cmdk `Command` primitives inside a Radix Popover — the same searchable-menu
 * stack the rest of the app uses — so it supports type-to-filter, grouped
 * sections with divider separators, and a check on the selected option.
 *
 * Purely presentational — the caller owns state.
 */
import { Fragment, useState } from 'react'
import { PiCaretDown, PiCheck } from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'components/UI/command'

export interface MappingOption {
  value: string
  label: string
}

export interface MappingGroup {
  /** Optional section label (e.g. "Identity", "Company"). Omit for a flat list. */
  label?: string
  options: MappingOption[]
}

interface MappingSelectProps {
  value: string
  onChange: (value: string) => void
  groups: MappingGroup[]
  /** Shown in the trigger when nothing is selected (e.g. "Select a column…"). */
  placeholder: string
  ariaLabel: string
  /** Show a type-to-filter search box at the top of the menu. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Copy for the empty state when a search matches nothing. Defaults to a
   *  generic string so it reads correctly whatever this select is choosing. */
  noResultsText?: string
  /** Render the trigger with the error (red) border to flag a missing choice. */
  invalid?: boolean
}

// DS2026 menu item: small body text, comfortable hit area, gray hover/active.
// `!` important overrides the pistachio defaults baked into the base CommandItem.
const ITEM_CLASS =
  '!cursor-pointer !gap-2 !rounded-lg !px-3 !py-1.5 !text-sm !font-medium !text-neutral-700 data-[selected=true]:!bg-neutral-100 data-[selected=true]:!text-neutral-900'

// Group heading: tailwind gray, small. Divider (CommandSeparator) sets sections
// apart instead of a filled background band.
const GROUP_CLASS =
  '!p-1.5 [&_[cmdk-group-heading]]:!px-3 [&_[cmdk-group-heading]]:!font-semibold [&_[cmdk-group-heading]]:!text-neutral-500'

export function MappingSelect({
  value,
  onChange,
  groups,
  placeholder,
  ariaLabel,
  searchable = false,
  searchPlaceholder = 'Search…',
  noResultsText = 'No results found',
  invalid = false,
}: MappingSelectProps) {
  const [open, setOpen] = useState(false)
  const selected = groups
    .flatMap((g) => g.options)
    .find((o) => o.value === value && o.value !== '')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-lg border bg-surface py-2 pl-3 pr-2.5 text-sm text-fg transition-colors hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
            invalid
              ? 'border-error-strong'
              : 'border-line-form data-[state=open]:border-brand-active'
          )}
        >
          <span className={cn('truncate', !selected && 'text-fg-secondary')}>
            {selected ? selected.label : placeholder}
          </span>
          <PiCaretDown className="size-4 shrink-0 text-fg-tertiary" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="min-w-[var(--radix-popover-trigger-width)] max-w-sm overflow-hidden rounded-xl border border-neutral-200 bg-surface p-0 shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.10),0px_2px_4px_-2px_rgba(16,24,40,0.06)]"
      >
        <Command>
          {searchable && (
            <CommandInput placeholder={searchPlaceholder} className="text-sm" />
          )}
          <CommandList className="max-h-72">
            <CommandEmpty className="px-3 py-4 text-center text-sm text-fg-secondary">
              {noResultsText}
            </CommandEmpty>
            {groups.map((group, gi) => (
              <Fragment key={group.label ?? `group-${gi}`}>
                {gi > 0 && (
                  <CommandSeparator className="!mx-0 !bg-neutral-200" />
                )}
                <CommandGroup heading={group.label} className={GROUP_CLASS}>
                  {group.options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      className={ITEM_CLASS}
                      onSelect={() => {
                        onChange(opt.value)
                        setOpen(false)
                      }}
                    >
                      <PiCheck
                        className={cn(
                          'size-4 shrink-0 text-brand-active',
                          opt.value !== value && 'opacity-0'
                        )}
                      />
                      <span className="whitespace-nowrap">{opt.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Fragment>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
